import classNames from 'classnames';
import { getActivityStatus } from 'utils';
import { isCHFReading } from 'utils/get-reading-info';
import { LATENT_READING_DIFF_HOURS } from 'infrastructure/consts';
import { getDiffInHours } from 'infrastructure/functions';
import CHFRenalStatus from 'components/atoms/chf-renal-status';
import useUser from 'utils/useUser';
import { ReadingStatus, ReadingType } from 'infrastructure/enums';

import s from './styles.module.scss';
import RecoveredStatus from './recovered-status';
import LatentReadingStatus from './latent-reading-status';

import type {
  TMightBeErroneousReason,
  TThresholdName,
} from 'infrastructure/interfaces/i-rpm-patient-activity';
import type { IPatientThresholds } from 'infrastructure/interfaces';
import type { FC } from 'react';

interface StatusCellProps {
  globalStatus: ReadingStatus;
  thresholdName?: TThresholdName | null;
  thresholds: IPatientThresholds;
  mightBeErroneousReason?: TMightBeErroneousReason;
  isRecovered?: boolean;
  wrapperClassName?: string;
  receivedTime?: string;
  readingTime?: string;
  readingType: ReadingType;
  timezone: string;
}

const StatusCell: FC<StatusCellProps> = ({
  globalStatus,
  thresholdName,
  thresholds,
  mightBeErroneousReason,
  isRecovered,
  wrapperClassName,
  receivedTime,
  readingTime,
  timezone,
  readingType,
}) => {
  const { hasRNRole } = useUser();
  const status = getActivityStatus(globalStatus);
  const isCHF = isCHFReading(thresholdName);
  const showCHFLabel = !isRecovered && thresholdName && isCHF && hasRNRole;
  const showWeightMgmtLabel =
    !isCHF &&
    globalStatus === ReadingStatus.Critical &&
    readingType === ReadingType.Weight;

  const diffTime = getDiffInHours({
    biggerDate: receivedTime,
    date: readingTime,
  });

  const showLatentStatus = diffTime
    ? diffTime >= LATENT_READING_DIFF_HOURS
    : false;

  return (
    <div className={classNames(s.block, wrapperClassName)} data-status>
      {showLatentStatus && receivedTime && (
        <LatentReadingStatus receivedTime={receivedTime} timezone={timezone} />
      )}
      <p
        data-cy="reading-status"
        className={classNames(s.status, s[`status-${globalStatus}`])}
      >
        {status}
      </p>
      {isRecovered && <RecoveredStatus />}
      {showCHFLabel && (
        <CHFRenalStatus
          thresholdName={thresholdName}
          thresholds={thresholds}
          mightBeErroneousReason={mightBeErroneousReason}
          isSubStatus
        />
      )}
      {showWeightMgmtLabel && (
        <CHFRenalStatus
          thresholdName={thresholdName}
          thresholds={thresholds}
          mightBeErroneousReason={mightBeErroneousReason}
          isSubStatus
          label="weightMgmt"
        />
      )}
    </div>
  );
};
export default StatusCell;
